import * as React from 'react'
import * as Switch from '@radix-ui/react-switch'
import { BodyText, LockIcon } from 'design-configuration/components'
import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'

const switchToggleVariants = cva('relative flex-shrink-0 cursor-pointer rounded-[19px] transition-all duration-200', {
  variants: {
    variant: {
      default: 'bg-primary-20 data-[state=checked]:bg-primary-80',
      inverted:
        'bg-surface-container-inverted border border-primary-inverted opacity-50 data-[state=checked]:bg-primary-inverted-20 data-[state=checked]:opacity-100 data-[state=checked]:border-0',
      light: 'bg-primary-inverted-10 data-[state=checked]:bg-primary-inverted-20'
    },
    size: {
      default: 'h-sm w-lg'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
})

const switchThumbVariants = cva('box-border block rounded-[19px] transition-transform duration-200', {
  variants: {
    variant: {
      default: 'bg-surface data-[state=checked]:bg-surface',
      inverted: 'bg-primary-inverted-20 border border-primary-inverted data-[state=checked]:bg-primary-inverted',
      light: 'bg-primary-inverted-10 data-[state=checked]:bg-primary-inverted'
    },
    size: {
      default: 'h-[12px] w-[12px] data-[state=checked]:translate-x-[18px] data-[state=unchecked]:translate-x-[2px]'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
})

export interface SwitchToggleProps
  extends Switch.SwitchProps,
    VariantProps<typeof switchToggleVariants>,
    VariantProps<typeof switchThumbVariants> {
  leftLabel?: string
  rightLabel?: string
  switchedRight: boolean
  onSwitchChange: (checked: boolean | 'indeterminate') => void
  className?: string
  fullLeft?: boolean
  altLabelLeftText?: string
}

const SwitchToggle = React.forwardRef<React.ElementRef<typeof Switch.Root>, SwitchToggleProps>(
  (
    {
      leftLabel,
      rightLabel,
      switchedRight,
      onSwitchChange,
      className,
      fullLeft,
      altLabelLeftText,
      disabled,
      variant,
      size,
      ...props
    },
    ref
  ) => {
    return (
      <div className={cn('gap-x-xs flex items-center', className)}>
        {leftLabel && (
          <div title={altLabelLeftText}>
            <button
              className={cn(
                'gap-x-xs flex flex-shrink-0 items-center text-right',
                variant === 'light' ? 'text-on-surface-inverted' : '',
                fullLeft ? '' : 'w-auto'
              )}
              onClick={() => onSwitchChange(!switchedRight)}
              title={altLabelLeftText}
            >
              <BodyText className={disabled ? 'text-on-surface-inverted-variant' : ''}>{leftLabel}</BodyText>
              {disabled ? <LockIcon className="w-sm h-sm fill-on-surface-variant" /> : null}
            </button>
          </div>
        )}
        <Switch.Root
          ref={ref}
          className={cn(switchToggleVariants({ variant, size }), disabled ? 'opacity-50' : '')}
          id="switch-toggle"
          checked={switchedRight}
          onCheckedChange={onSwitchChange}
          onClick={(event) => {
            event.stopPropagation()
          }}
          {...props}
        >
          <Switch.Thumb className={switchThumbVariants({ variant, size })} />
        </Switch.Root>
        {rightLabel ? (
          <BodyText className={cn('w-1/2 flex-shrink-0', variant === 'light' ? 'text-on-surface-inverted' : '')}>
            {rightLabel}
          </BodyText>
        ) : null}
      </div>
    )
  }
)

SwitchToggle.displayName = 'SwitchToggle'

export default SwitchToggle
